import { Ref, useCallback, useContext, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { PixelRatio, Platform, StyleSheet, View } from 'react-native';
import { BasicScrollView } from "@/components/BasicScrollView";
import { Button, Panel, Tabs, TextInput } from 'react95-native-rabbl';
import { AppText } from '@/components/AppText';
import { CustomDivider } from "@/components/CustomDivider";
import Head from 'expo-router/head';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import { Link } from 'expo-router';
import Footer from '@/components/Footer';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppUtils from '@/utilities/AppUtils';
import { useRouter } from 'expo-router';
import { AppContext } from '@/components/AppContext';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { setLoggedIn, setShowTest } from '@/state/user';
import Constants from 'expo-constants';
import * as Device from 'expo-device';
import JSONTree, { Renderable } from 'react-native-json-tree'
import { UAParser } from 'ua-parser-js';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    paddingTop: 10,
    gap: 20,
    backgroundColor: 'transparent',
  },
  tabWrapper: {
    gap: 20,
    flex: 1,
    alignItems: 'center',
  },
  left: {
    gap: 20,
    width: '100%',
  },
  elementWrapper: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
  },
  flexRow: {
    flexDirection: 'row',
  },
  formElement: {
    width: '100%',
  },
  log: {
    height: 300,
  },
  panel: {
    padding: 20,
    gap: 20,
    //alignItems: 'center',
  },
});

interface CustomJSONTreeProps {
  data: Renderable
}

// https://github.com/reduxjs/redux-devtools/tree/75322b15ee7ba03fddf10ac3399881e302848874/src/react/themes
const theme = {
  base00: '#c6c6c6',
  base01: '#292824',
  base02: '#292824',
  base03: '#292824',
  base04: '#292824',
  base05: '#292824',
  base06: '#292824',
  base07: '#292824',
  base08: '#292824',
  base09: '#292824',
  base0A: '#292824',
  base0B: '#292824',
  base0C: '#292824',
  base0D: '#292824',
  base0E: '#292824',
  base0F: '#292824'
};

function CustomJSONTree({data} : CustomJSONTreeProps) : React.ReactNode {
  return (
    <JSONTree
      data={data}
      theme={theme}
      invertTheme={false}
      hideRoot={true}
    />
  );
}

type IInput = {
  getDescription: () => string;
};

type InputProps = {
  ref?: Ref<IInput>,
};

function Input({ ref }: InputProps) {
  const [description, setDescription] = useState('');

  useImperativeHandle(ref, () => ({
    getDescription: () => {
      return description;
    },
  }), [description]);

  return (
    <TextInput
      value={description}
      onChangeText={setDescription}
      placeholder="Details"
      returnKeyType="next"
      multiline={true}
      numberOfLines={6}
      style={AppConstants.STYLES.textInputMultiline}
    />
  );
}

export default function Diagnostics() {
  const [debug, setDebug] = useState(AppUtilities.getAccumulatedDebug());
  const { showSpinner, hideSpinner } = useContext(AppContext);
  const router = useRouter();
  const dispatch = useDispatch();
  const reduxState = useSelector((state: RootState) => state);
  const showTest = useSelector((state: RootState) => state.user.showTest);
  const inputRef = useRef<IInput>(null);
  const [successMessage, setSuccessMessage] = useState("");

  function refresh() {
    AppUtilities.info("Refreshed");
    reloadDebug();
    AppUtils.showInformationalToast('Successfully refreshed diagnostic log');
  }

  function reloadDebug() {
    setDebug(AppUtilities.getAccumulatedDebug());
  }

  function throwException() {
    throw new Error("Threw a dummy exception");
  }

  const submitDebug = useCallback(async () => {
    try {
      showSpinner();
      setSuccessMessage("");

      // context, uri, method, postJson, formUrlEncoded, skipAudit, skipErrorStatusHandling
      const result = await AppUtilities.fetchUnauthorized(
        "Diagnostics.submitDebug",
        "/rabbl/api/senddiag",
        "POST",
        {
          user_input: inputRef.current?.getDescription(),
          state: reduxState,
          accumulatedDebug: debug,
        },
        null,
        true,
      );

      const msg = 'Diagnostics submitted';
      AppUtils.showInformationalToast(msg);
      setSuccessMessage(msg);
    } catch (e) {
      AppUtils.showErrorToast(AppUtilities.getErrors(e), () => submitDebug(), true);
      AppUtilities.handleException(e, "Diagnostics.submitDebug");
    } finally {
      hideSpinner();
    }
  }, [debug, reduxState, inputRef]);

  useEffect(() => {
    // On every re-render
    reloadDebug();
  });

  const logout = async () => {
    await AppUtils.logout(dispatch, showSpinner, hideSpinner);
    AppUtils.showInformationalToast('Successfully logged out');
    router.navigate("/");
  };

  const toggleShowTest = async () => {
    dispatch(setShowTest(!showTest));
    AppUtils.showInformationalToast('Now ' + (showTest ? 'hiding' : 'showing') + ' test data');
  };

  const renderLogicalUserAgentInfo = useCallback(() => {
    const logicalUserAgentInfo = AppUtils.getLogicalUserAgent();
    if (logicalUserAgentInfo) {
      return (
        <>
          <CustomText>User agent</CustomText>
          <CustomJSONTree data={logicalUserAgentInfo as any} />
        </>
      );
    } else {
      try {
        UAParser();
      } catch (e) {
        return (
          <>
            <CustomText>User agent error</CustomText>
            <CustomJSONTree data={e as any} />
          </>
        );
      }
      
      return null;
    }
  }, []);

  return (
    <BasicScrollView center={false}>
      <Meta title={"Diagnostics"} />
      <AppText>Describe your issue and press 'Send Diagnostics' for us to review:</AppText>
      <View style={styles.elementWrapper}>
        <Input ref={inputRef} />
      </View>
      <View style={styles.elementWrapper}>
        <Button onPress={submitDebug}>Send Diagnostics</Button>
      </View>
      {
        successMessage.length > 0 && (
          <AppText selectable={true} style={AppConstants.STYLES.fontBoldColorGreen}>{successMessage}</AppText>
        )
      }
      <CustomDivider margin={true} fullWidth={true} />
      <CustomText>Advanced Diagnostics</CustomText>
      <Panel variant='raised' elevation={4}>
        <View style={styles.panel}>
          <CustomText>Log</CustomText>
          <TextInput
            value={debug}
            multiline={true}
            style={styles.log}
          />
          <CustomText>Advanced Actions</CustomText>
          <View style={styles.flexRow}>
            <Button onPress={refresh}>Refresh Log</Button>
          </View>
          {/*<View style={styles.flexRow}>
            <Button onPress={toggleShowTest}>{showTest ? "Hide test data" : "Show test data"}</Button>
          </View>*/}
          {/*user_info && user_info.user_info_id != -1 && (
            <>
              <Divider />
              <AppText>{user_info.email}</AppText>
            </>
          )*/}
          { /*<Button onPress={logout} disabled={!loggedIn}>Logout</Button>*/ }
          <View style={styles.left}>
            <CustomText>State</CustomText>
            <CustomJSONTree data={reduxState} />
          </View>
          <View style={styles.left}>
            <CustomText>React Native Platform</CustomText>
            <CustomJSONTree data={{
              os: Platform.OS,
              version: Platform.Version
            }} />
            <CustomText>Expo Constants</CustomText>
            <CustomJSONTree data={{
              deviceName: Constants.deviceName ?? 'No device name',
              sessionId: Constants.sessionId,
              statusBarHeight: Constants.statusBarHeight,
              systemFonts: Constants.systemFonts,
              systemVersion: Constants.systemVersion ?? 'No system version',
            }} />
            <CustomText>Expo Device</CustomText>
            <CustomJSONTree data={Device} />
            { renderLogicalUserAgentInfo() }
            <CustomText>Other information</CustomText>
            <AppText>Pixel ratio: { PixelRatio.get() }</AppText>
          </View>
          <CustomDivider margin={true} fullWidth={true} />
          <View style={{ flexDirection: "row", alignSelf: "center" }}><Button onPress={() => AppUtils.showInformationalToast("Toast")}>Test toast</Button></View>
          <View style={{ flexDirection: "row", alignSelf: "center" }}><Link href="/error" asChild={true} className={"nontext"}><Button>:-(</Button></Link></View>
          {/*<View style={{ flexDirection: "row", alignSelf: "center" }}><Button onPress={throwException}>:-*</Button></View>*/}
        </View>
      </Panel>
      
      <Footer />
    </BasicScrollView>
  );
}
